import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { PitchPlanBadge } from "@/components/bookings/PitchPlanBadge";
import { PitchOptionPicker } from "@/components/bookings/PitchOptionPicker";
import { PitchConflictModal } from "@/components/bookings/PitchConflictModal";
import { StayRelocationPanel } from "@/components/bookings/StayRelocationPanel";
import { api } from "@/lib/apiClient";
import {
  assignPitchWithOptions,
  buildAssignOptionsFromSolution,
  ensurePitchSuggestion,
  fetchPitchOptions,
  unfixPitch,
  type PitchOption,
  type PitchOptionSolution,
  type PitchOptionsResponse,
} from "@/lib/pitchAssignmentOptions";

import {
  isPitchFixed,
  pitchPlanStatus,
} from "@/lib/pitchAssignment";
import type { Booking, Pitch } from "@/types/camping";

export interface PitchPlanPanelProps {
  booking: Booking;
  pitches: Pitch[];
  canEdit?: boolean;
  draftArrival?: string;
  draftDeparture?: string;
  onUpdated: (booking: Booking) => void;
  onOpenPitch?: (pitch: Pitch) => void;
}

export function PitchPlanPanel({
  booking,
  pitches,
  canEdit = false,
  draftArrival,
  draftDeparture,
  onUpdated,
  onOpenPitch,
}: PitchPlanPanelProps) {
  const [selectedPitchId, setSelectedPitchId] = useState(booking.pitch_id ?? "");
  const [loading, setLoading] = useState<
    "assign" | "fix" | "suggest" | "ensure" | "unfix" | "change-fixed" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [fixedAction, setFixedAction] = useState<"change" | "unfix" | null>(null);
  const [changeReason, setChangeReason] = useState("");
  const [conflictOption, setConflictOption] = useState<PitchOption | null>(null);
  const [suggestionEnsured, setSuggestionEnsured] = useState(false);
  const [preloadedOptions, setPreloadedOptions] = useState<PitchOptionsResponse | null>(null);

  const fixed = isPitchFixed(booking.pitch_assignment_mode);
  const isCheckedIn = booking.status === "checked_in";
  const pitch = booking.pitch_id ? pitches.find((p) => p.id === booking.pitch_id) : undefined;
  const status = pitchPlanStatus(booking);

  useEffect(() => {
    setSelectedPitchId(booking.pitch_id ?? "");
    setError(null);
    setFixedAction(null);
    setChangeReason("");
    setShowPicker(false);
    setPreloadedOptions(null);
  }, [booking.id, booking.pitch_id, booking.pitch_assignment_mode]);

  useEffect(() => {
    if (!canEdit || isCheckedIn) return;

    let cancelled = false;
    void fetchPitchOptions(booking.id)
      .then((response) => {
        if (!cancelled) setPreloadedOptions(response);
      })
      .catch(() => {
        if (!cancelled) setPreloadedOptions(null);
      });

    return () => {
      cancelled = true;
    };
  }, [booking.id, canEdit, isCheckedIn]);

  useEffect(() => {
    if (!canEdit || fixed || isCheckedIn || suggestionEnsured) return;
    if (booking.pitch_id) {
      setSuggestionEnsured(true);
      return;
    }
    if (booking.pitch_assignment_mode === "fixed") return;

    let cancelled = false;
    setLoading("ensure");
    void ensurePitchSuggestion(booking.id)
      .then((updated) => {
        if (!cancelled) {
          onUpdated(updated);
          setSuggestionEnsured(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSuggestionEnsured(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    booking.id,
    booking.pitch_id,
    booking.pitch_assignment_mode,
    canEdit,
    fixed,
    isCheckedIn,
    onUpdated,
    suggestionEnsured,
  ]);

  async function assignPitch(
    pitchId: string,
    options: {
      reassign_conflicts?: boolean;
      ignore_rules?: boolean;
      ignore_category?: boolean;
      reason?: string;
    } = {},
  ) {
    setLoading(fixed ? "change-fixed" : "assign");
    setError(null);
    try {
      const updated = await assignPitchWithOptions(booking.id, pitchId, options);
      onUpdated(updated);
      setConflictOption(null);
      setShowPicker(false);
      setFixedAction(null);
      setChangeReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzzuweisung fehlgeschlagen.");
    } finally {
      setLoading(null);
    }
  }

  async function submitUnfix() {
    const reason = changeReason.trim();
    if (!reason) {
      setError("Bitte geben Sie eine Begründung an.");
      return;
    }
    setLoading("unfix");
    setError(null);
    try {
      const updated = await unfixPitch(booking.id, reason);
      onUpdated(updated);
      setFixedAction(null);
      setChangeReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Aufheben der Fixierung fehlgeschlagen.");
    } finally {
      setLoading(null);
    }
  }

  async function submitFixedPitchChange(pitchId: string) {
    const reason = changeReason.trim();
    if (!reason) {
      setError("Bitte geben Sie eine Begründung an.");
      return;
    }
    await assignPitch(pitchId, { reason });
  }

  async function fixPitch() {
    const pitchId = selectedPitchId || booking.pitch_id;
    if (!pitchId) {
      setError("Bitte zuerst einen Stellplatz wählen.");
      return;
    }
    setLoading("fix");
    setError(null);
    try {
      const updated = await api.post<Booking>(`/api/v1/bookings/${booking.id}/fix-pitch`, {
        pitch_id: pitchId,
      });
      onUpdated(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Platzfixierung fehlgeschlagen.");
    } finally {
      setLoading(null);
    }
  }

  async function suggestPitch() {
    setLoading("suggest");
    setError(null);
    try {
      const updated = await ensurePitchSuggestion(booking.id, { force: true });
      onUpdated(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Vorschlag konnte nicht berechnet werden.");
    } finally {
      setLoading(null);
    }
  }

  function handleSelectPitch(pitchId: string, _option: PitchOption) {
    setSelectedPitchId(pitchId);
  }

  function handleBlockedPitchSelect(option: PitchOption) {
    setConflictOption(option);
  }

  async function handleApplySolution(option: PitchOption, solution: PitchOptionSolution) {
    const assignOptions = buildAssignOptionsFromSolution(option, solution);
    setSelectedPitchId(option.pitch_id);
    if (fixed) {
      const reason = changeReason.trim();
      if (!reason) {
        setError("Bitte geben Sie eine Begründung an.");
        return;
      }
      await assignPitch(option.pitch_id, { ...assignOptions, reason });
      return;
    }
    await assignPitch(option.pitch_id, assignOptions);
  }

  const pitchLabel = pitch
    ? `${pitch.code}${pitch.label ? ` · ${pitch.label}` : ""}`
    : "Noch kein Stellplatz";

  const busy = loading !== null;

  return (
    <section className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-label">Stellplatzplanung</h3>
        <PitchPlanBadge booking={booking} />
      </div>

      <div className="text-sm text-label">
        {pitch && onOpenPitch ? (
          <button
            type="button"
            className="font-medium text-accent hover:underline"
            onClick={() => onOpenPitch(pitch)}
          >
            {pitchLabel}
          </button>
        ) : (
          <span className="font-medium">{pitchLabel}</span>
        )}
        {status === "occupied" ? (
          <p className="mt-1 text-xs text-label-secondary">
            Der Gast ist eingecheckt. Automatisch wird der Platz nicht mehr geändert. Über
            „Umziehen“ wechselt er ab einem Tag auf einen anderen Platz oder in eine andere
            Kategorie.
          </p>
        ) : null}
        {status === "suggested" ? (
          <p className="mt-1 text-xs text-label-secondary">
            Vorgeschlagener Platz – bleibt bestehen, bis ein anderer Vorschlag nötig wird oder Sie
            ihn manuell neu berechnen oder fixieren.
          </p>
        ) : null}
        {status === "open" && loading === "ensure" ? (
          <p className="mt-1 text-xs text-label-secondary">Stellplatzvorschlag wird berechnet…</p>
        ) : null}
        {status === "fixed" ? (
          <p className="mt-1 text-xs text-label-secondary">
            Platz ist verbindlich fixiert und wird nicht mehr automatisch umgeplant.
          </p>
        ) : null}
      </div>

      {canEdit && fixed && !isCheckedIn ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setFixedAction((current) => {
                  const next = current === "change" ? null : "change";
                  if (next !== "change") setShowPicker(false);
                  return next;
                });
                setError(null);
              }}
            >
              {fixedAction === "change" ? "Änderung abbrechen" : "Platz ändern"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setFixedAction((current) => (current === "unfix" ? null : "unfix"));
                setError(null);
                setShowPicker(false);
              }}
            >
              {fixedAction === "unfix" ? "Abbrechen" : "Fixierung aufheben"}
            </Button>
          </div>

          {fixedAction ? (
            <label className="block text-sm">
              <span className="mb-1 block text-label-secondary">Begründung *</span>
              <textarea
                className="eh-input min-h-[4rem] resize-y"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                rows={2}
                disabled={busy}
                placeholder="z. B. Gastwunsch, technischer Defekt am Platz, Konflikt mit Nachbarplatz …"
              />
            </label>
          ) : null}

          {fixedAction === "change" ? (
            <div className="space-y-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setShowPicker((v) => !v)}
              >
                {showPicker ? "Auswahl schließen" : "Neuen Stellplatz wählen"}
              </Button>
              {showPicker ? (
                <PitchOptionPicker
                  booking={booking}
                  selectedPitchId={selectedPitchId}
                  onSelectPitch={handleSelectPitch}
                  onBlockedPitchSelect={handleBlockedPitchSelect}
                  preloadedOptions={preloadedOptions}
                />
              ) : null}
              {selectedPitchId && selectedPitchId !== booking.pitch_id ? (
                <Button
                  type="button"
                  size="sm"
                  loading={loading === "change-fixed"}
                  disabled={busy}
                  onClick={() => void submitFixedPitchChange(selectedPitchId)}
                >
                  Fixierten Platz ändern
                </Button>
              ) : null}
            </div>
          ) : null}

          {fixedAction === "unfix" ? (
            <div className="space-y-2">
              <p className="text-xs text-label-secondary">
                Die Fixierung wird aufgehoben. Der aktuelle Platz bleibt als Vorschlag erhalten und
                kann wieder automatisch umgeplant werden.
              </p>
              <Button
                type="button"
                size="sm"
                loading={loading === "unfix"}
                disabled={busy}
                onClick={() => void submitUnfix()}
              >
                Fixierung aufheben
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {canEdit && !fixed && !isCheckedIn ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={loading === "suggest"}
              disabled={busy}
              onClick={() => void suggestPitch()}
            >
              Vorschlag neu berechnen
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setShowPicker((v) => !v)}
            >
              {showPicker ? "Auswahl schließen" : "Stellplatz manuell wählen"}
            </Button>
            {selectedPitchId && selectedPitchId !== booking.pitch_id ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={loading === "assign"}
                disabled={busy}
                onClick={() => void assignPitch(selectedPitchId)}
              >
                Als Vorschlag setzen
              </Button>
            ) : null}
            {selectedPitchId || booking.pitch_id ? (
              <Button
                type="button"
                size="sm"
                loading={loading === "fix"}
                disabled={busy}
                onClick={() => void fixPitch()}
              >
                Platz fixieren
              </Button>
            ) : null}
          </div>

          {showPicker ? (
            <PitchOptionPicker
              booking={booking}
              selectedPitchId={selectedPitchId}
              onSelectPitch={handleSelectPitch}
              onBlockedPitchSelect={handleBlockedPitchSelect}
              preloadedOptions={preloadedOptions}
            />
          ) : (
            <p className="text-xs text-label-tertiary">
              {pitches.length} Stellplätze – Auslastung und Umplanungsmöglichkeiten werden im
              Hintergrund berechnet. Empfohlene Plätze füllen Lücken optimal; weitere funktionieren,
              sind aber weniger ideal. Plätze mit Umplanung erfordern das Verschieben von Vorschlägen.
            </p>
          )}
        </div>
      ) : null}

      <StayRelocationPanel
        booking={booking}
        pitches={pitches}
        canEdit={canEdit}
        draftArrival={draftArrival}
        draftDeparture={draftDeparture}
        onUpdated={onUpdated}
      />

      <PitchConflictModal
        open={conflictOption !== null}
        option={conflictOption}
        loading={loading === "assign" || loading === "change-fixed"}
        onClose={() => setConflictOption(null)}
        onApplySolution={(option, solution) => void handleApplySolution(option, solution)}
      />

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </section>
  );
}
